<template>
  <div :class="`month first-day-of-week--${firstDayOfWeek}`">
    <div class="month-name" v-html="label"></div>
    <div class="weekdays">
      <div v-for="weekday in weekdays" class="weekday">{{ weekday }}</div>
    </div>
    <div v-for="i in numberDays"
         :key="i"
         :class="dayClasses(i)"
         :style="dayStyle(i)"
         :title="title(i)"
         class="day"
         @mousedown="selectDate(i)"
         @mouseup="debounceSelectDate($event, i)"
         @mouseover="hoverDate($event, i)"
         @touchstart="selectDate(i)"
         @touchend="debounceSelectDate($event, i)"
         @touchmove="hoverDate($event, i)">
      <span>{{ i }}</span>
    </div>
  </div>
</template>

<script>
import fecha from 'fecha';
import { debounce, isEmpty, isNil } from 'lodash';
import TimerMixin from '../../../mixins/TimerMixin';

export default {
  name: 'Month',

  mixins: [TimerMixin],

  props: {
    label: {
      type: String,
      default: '',
    },
    firstDayOfWeek: {
      type: String,
      default: 'mo',
    },
    weekdays: {
      type: Array,
    },
    firstDayOfMonth: {
      type: Date,
      default: () => new Date(),
    },
    selectedDates: {
      type: Array,
      default: () => [],
    },
    minDate: {
      type: String,
      default: null,
    },
    maxDate: {
      type: String,
      default: null,
    },
    dateColors: {
      type: Object,
      default: () => ({}),
    },
    seasonDates: {
      type: Object,
      default: () => ({}),
    },
  },

  data: function () {
    return {
      todayFormated: fecha.format(this.$parent.today, 'YYYY-MM-DD'),
    };
  },

  computed: {
    numberDays: function () {
      return this.isNil(this.firstDayOfMonth) ? 30 : new Date(this.firstDayOfMonth.getYear() + 1900, this.firstDayOfMonth.getMonth() + 1, 0).getDate();
    },
    firstDayOfMonthClass: function () {
      if (this.firstDayOfMonth == null) return null;

      const prefix = '';

      // eslint-disable-next-line default-case
      switch (this.firstDayOfMonth.getDay()) {
        case 1: return `${prefix}mo`;
        case 2: return `${prefix}tu`;
        case 3: return `${prefix}we`;
        case 4: return `${prefix}th`;
        case 5: return `${prefix}fr`;
        case 6: return `${prefix}sa`;
        case 0: return `${prefix}su`;
      }

      return this.firstDayOfMonth.getDay();
    },
    isoMonthPrefix: function () {
      if (this.firstDayOfMonth == null) return null;

      return fecha.format(this.firstDayOfMonth, 'YYYY-MM-');
    },
    todayMidnight() {
      const date = new Date(this.$parent.today);
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);

      return date;
    },
  },

  methods: {
    hoverDate: function ($event, i) {
      if (!isEmpty($event.changedTouches)) {
        const elem = document.elementFromPoint($event.changedTouches[0].clientX, $event.changedTouches[0].clientY);
        if (!isNil(elem)) {
          i = elem.textContent.trim();
        }
      }
      $event.preventDefault();
      this.$emit('hoverdate', i);
    },
    selectDate: function (i) {
      this.$emit('selectdate', i);
      this.clearTimeout(this.selectedTimer);
      this.selected = i;
      this.selectedTimer = this.setTimeout(() => {
        this.selected = null;
      }, 500);
    },
    debounceSelectDate: function ($event, i) {
      if (!isEmpty($event.changedTouches)) {
        const elem = document.elementFromPoint($event.changedTouches[0].clientX, $event.changedTouches[0].clientY);
        if (!isNil(elem)) {
          i = elem.textContent.trim();
        }
      }
      if (this.selected !== i) this.$emit('selectdate', i);
    },
    isNil(val) {
      return (val === null || typeof val === 'undefined');
    },

    title(i) {
      const dayString = `${this.isoMonthPrefix}${this.pad(i, 2)}`;
      const seasonDate = isNil(this.seasonDates) ? null : this.seasonDates[dayString];
      return isEmpty(seasonDate) || isEmpty(seasonDate.season) ? '-' : seasonDate.season;
    },
    dayStyle(i) {
      const dayString = `${this.isoMonthPrefix}${this.pad(i, 2)}`;
      const color = this.dateColors[dayString];

      if (isEmpty(color)) return {};

      if (!isNil(this.minDate) && dayString < this.minDate) {
        return {};
      }

      if (!isNil(this.maxDate) && dayString > this.maxDate) {
        return {};
      }

      return {
        'background-color': `rgba(${this.toRGB(color)}, 0.6)`,
      };
    },
    dayClasses(i) {
      const ret = [];
      if (i === 1) {
        ret.push(this.firstDayOfMonthClass);
      }

      const dayString = `${this.isoMonthPrefix}${this.pad(i, 2)}`;

      if (dayString === this.todayFormated) {
        ret.push('today');
      }

      /*
      if (fecha.parse(dayString, 'YYYY-MM-DD') < this.todayMidnight) {
        ret.push('past');
      }
      */

      if (this.$parent.disabledDates.includes(dayString) && !this.$parent.departureOnlyDates.includes(dayString)) {
        ret.push('disabled');
      }

      if (this.$parent.arrivalOnlyDates.includes(dayString)) {
        ret.push('arrival-only');
      }

      if (this.$parent.departureOnlyDates.includes(dayString)) {
        ret.push('departure-only');
      }

      if (!isEmpty(this.selectedDates)) {
        if (this.selectedDates.indexOf(dayString) === 0) {
          ret.push('selected--start');
        }

        if ((this.selectedDates.indexOf(dayString) + 1) === this.selectedDates.length) {
          ret.push('selected--end');
        }

        if (this.selectedDates.indexOf(dayString) >= 0) {
          ret.push('selected');
        }
      }

      if (!isNil(this.minDate) && dayString < this.minDate) {
        ret.push('past');
      }

      if (!isNil(this.maxDate) && dayString > this.maxDate) {
        ret.push('past');
      }

      return ret;
    },
    pad(num, size) {
      let s = `${num}`;
      while (s.length < size) s = `0${s}`;
      return s;
    },
    toRGB(hexColorCode) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColorCode);
      return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '';
    },
  },
};
</script>

<style lang="scss" scoped>

</style>



// WEBPACK FOOTER //
// src/components/lib/calendar/Month.vue